function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import applyReducer from './applyReducer';
import constants from '../constants/annotationConstants';
import annotationSelectionConstants from '../constants/annotationSelectionConstants';
import { convertFormAnnotationToLegacyAnnotation } from '../utils/transformFormAnnotation';
import formConstants from '../constants/formConstants';
const initialState = {
  editorState: {
    remotePath: {
      _download: null,
      _cache: null
    },
    title: null
  },
  isLoading: false,
  error: null,
  groupedAnnotations: {},
  active: {}
};
const clearSelection = (state, action) => {
  if (action.annotationContext === 'form') {
    return _objectSpread(_objectSpread({}, state), {}, {
      active: {}
    });
  }
  return state;
};
const clearEditorState = () => _objectSpread({}, initialState);
const getTypeForAnnotation = ann => {
  if (ann.inkAnnotation) {
    return ann.inkAnnotation.unsmoothed ? 'Line' : 'Ink';
  }
  if (ann.highlightAnnotation) return 'Highlight';
  if (ann.freeTextAnnotation) return 'FreeText';
  if (ann.infoAnnotation) return 'Info';
  if (ann.noteAnnotation) return 'Note';
  return null;
};

// Group legacy-format annotations by page and type (e.g., Ink, Highlight, etc.)
const groupAnnotationsByPageAndType = annotations => {
  const grouped = {};
  if (!Array.isArray(annotations)) return grouped;
  annotations.forEach(ann => {
    const page = ann.pageNumber;
    const type = getTypeForAnnotation(ann);
    if (!page || !type) return;
    if (!grouped[page]) grouped[page] = {};
    if (!grouped[page][type]) grouped[page][type] = [];
    grouped[page][type].push(ann);
  });
  return grouped;
};

// Helper function to update groupedAnnotations with a new or existing annotation
const updateGroupedAnnotations = (groupedAnnotations, page, type, annotationId, updatedAnnotation) => {
  // Ensure the page and type structure exists
  const newGroupedAnnotations = _objectSpread({}, groupedAnnotations);
  if (!newGroupedAnnotations[page]) {
    newGroupedAnnotations[page] = {};
  }
  if (!newGroupedAnnotations[page][type]) {
    newGroupedAnnotations[page][type] = [];
  }

  // Check if the annotation already exists
  const existingIndex = newGroupedAnnotations[page][type].findIndex(ann => ann.id === annotationId);
  if (existingIndex >= 0) {
    // Update existing annotation - create new array
    newGroupedAnnotations[page][type] = newGroupedAnnotations[page][type].map(ann => ann.id === annotationId ? updatedAnnotation : ann);
  } else {
    // Add new annotation - create new array
    newGroupedAnnotations[page][type] = [...newGroupedAnnotations[page][type], updatedAnnotation];
  }
  return newGroupedAnnotations;
};

// Add a new annotation to groupedAnnotations
const addNewAnnotation = (state, action) => {
  const {
    annotation
  } = action.payload;

  // Convert the form annotation to legacy format for display
  let legacyAnnotation;
  try {
    legacyAnnotation = convertFormAnnotationToLegacyAnnotation(annotation);
  } catch (_unused) {
    return state;
  }
  if (!legacyAnnotation) {
    return state;
  }

  // Get the correct type for the annotation using the same logic as groupAnnotationsByPageAndType
  let annotationType;
  try {
    annotationType = getTypeForAnnotation(legacyAnnotation);
  } catch (_unused2) {
    return state;
  }

  // Use updateGroupedAnnotations to add the new annotation
  const newGroupedAnnotations = updateGroupedAnnotations(state.groupedAnnotations, legacyAnnotation.pageNumber, annotationType, legacyAnnotation.id, legacyAnnotation);
  const newState = _objectSpread(_objectSpread({}, state), {}, {
    groupedAnnotations: newGroupedAnnotations,
    active: legacyAnnotation // Set as active annotation
  });
  return newState;
};
const loadAnnotations = state => _objectSpread(_objectSpread({}, state), {}, {
  isLoading: true,
  error: null
});
const loadAnnotationsFailure = (state, action) => _objectSpread(_objectSpread({}, state), {}, {
  isLoading: false,
  error: action.payload
});
const loadAnnotationsSuccess = (state, action) => {
  const rawAnnotations = action.payload;

  // Transform all form annotations to legacy format
  const annotations = (rawAnnotations || []).map(convertFormAnnotationToLegacyAnnotation).filter(Boolean);
  const groupedAnnotations = groupAnnotationsByPageAndType(annotations);
  return _objectSpread(_objectSpread({}, state), {}, {
    isLoading: false,
    groupedAnnotations // only store grouped structure
  });
};
const loadPersistedState = (state, action) => _objectSpread(_objectSpread({}, state), {}, {
  editorState: action.payload,
  groupedAnnotations: {} // Clear groupedAnnotations when loading persisted state
});
const persistEditorState = (state, action) => _objectSpread(_objectSpread({}, state), {}, {
  editorState: action.payload
});
const select = (state, action) => {
  if (action.annotationContext === 'form') {
    return _objectSpread(_objectSpread({}, state), {}, {
      active: action.payload
    });
  }
  return state;
};
const setEditorState = (state, action) => _objectSpread(_objectSpread({}, state), {}, {
  editorState: action.payload,
  groupedAnnotations: {} // Clear groupedAnnotations when editor state changes
});

// Helper function to find an annotation by ID in groupedAnnotations
const findAnnotationInGroupedAnnotations = (groupedAnnotations, annotationId) => {
  const pages = Object.keys(groupedAnnotations);
  for (let i = 0; i < pages.length; i += 1) {
    const page = pages[i];
    const types = Object.keys(groupedAnnotations[page]);
    for (let j = 0; j < types.length; j += 1) {
      const type = types[j];
      const annotation = groupedAnnotations[page][type].find(ann => ann.id === annotationId);
      if (annotation) {
        return {
          foundAnnotation: annotation,
          foundPage: page,
          foundType: type
        };
      }
    }
  }
  return {
    foundAnnotation: null,
    foundPage: null,
    foundType: null
  };
};

// Helper function to apply form format changes to a legacy annotation
const applyFormChangesToLegacyAnnotation = (annotation, changes) => {
  const updatedAnnotation = _objectSpread({}, annotation);
  changes.forEach(change => {
    if (change.path === '/content/render/rect') {
      // Transform form rect back to legacy rectString format
      const {
        origin,
        size
      } = change.data;
      updatedAnnotation.rectString = `{{${origin.x}, ${origin.y}}, {${size.width}, ${size.height}}}`;
    } else if (change.path === '/content/render/pointArray') {
      // Transform form pointArray back to legacy pathString format
      if (!updatedAnnotation.inkAnnotation) {
        updatedAnnotation.inkAnnotation = {};
      }
      const pathString = change.data.map(point => `{${point.x}, ${point.y}}`).join('/');
      updatedAnnotation.inkAnnotation.pathString = pathString;
    }
  });
  return updatedAnnotation;
};

// Patch groupedAnnotations for individual form changes
const patchAnnotationsOnFormChange = (state, action) => {
  const {
    changeEvent: {
      content,
      content: {
        render: {
          page,
          renderType
        } = {},
        id
      } = {}
    }
  } = action;

  // Early return if required data is missing
  if (!page || !renderType || !id) {
    return state;
  }

  // Early return if the page or renderType group doesn't exist
  if (!state.groupedAnnotations[page] || !state.groupedAnnotations[page][renderType]) {
    return state;
  }

  // Transform the updated annotation to the legacy/editor shape
  const transformed = convertFormAnnotationToLegacyAnnotation(content);
  if (!transformed) {
    return state;
  }

  // Find the existing annotation to merge with
  const existingAnnotation = state.groupedAnnotations[page][renderType].find(ann => ann.id === id);

  // Patch groupedAnnotations
  const newGroupedAnnotations = updateGroupedAnnotations(state.groupedAnnotations, page, renderType, id, _objectSpread(_objectSpread({}, existingAnnotation), transformed));
  return _objectSpread(_objectSpread({}, state), {}, {
    groupedAnnotations: newGroupedAnnotations,
    active: transformed
  });
};

// Patch groupedAnnotations for batch changes
const patchAnnotationsOnFormBatchChange = (state, action) => {
  const {
    payload: changes
  } = action;

  // Early return if no valid changes
  if (!changes || !Array.isArray(changes) || changes.length === 0) {
    return state;
  }

  // Find the annotation ID from the changes (they should all be for the same annotation)
  const firstChange = changes[0];
  if (!firstChange || !firstChange.id) {
    return state;
  }
  const annotationId = firstChange.id;

  // Find the annotation in groupedAnnotations
  const {
    foundAnnotation,
    foundPage,
    foundType
  } = findAnnotationInGroupedAnnotations(state.groupedAnnotations, annotationId);
  if (!foundAnnotation) {
    return state;
  }

  // Apply all changes to the annotation
  const updatedAnnotation = applyFormChangesToLegacyAnnotation(foundAnnotation, changes);

  // Update the groupedAnnotations
  const newGroupedAnnotations = updateGroupedAnnotations(state.groupedAnnotations, foundPage, foundType, annotationId, updatedAnnotation);
  return _objectSpread(_objectSpread({}, state), {}, {
    groupedAnnotations: newGroupedAnnotations,
    active: updatedAnnotation
  });
};

// Revert groupedAnnotations when form changes are undone
const revertAnnotationsOnFormUndo = (state, action) => {
  // If we have the reverted form state, use it to update the annotation display
  if (action.payload && action.payload.revertedFormState) {
    const {
      revertedFormState
    } = action.payload;
    const {
      content
    } = revertedFormState;
    if (!content) {
      return _objectSpread(_objectSpread({}, state), {}, {
        active: {}
      });
    }
    const {
      render: {
        page,
        renderType
      } = {}
    } = content;
    const {
      id
    } = revertedFormState;

    // Early return if required data is missing
    if (!page || !renderType || !id) {
      return _objectSpread(_objectSpread({}, state), {}, {
        active: {}
      });
    }

    // Early return if the page or renderType group doesn't exist
    if (!state.groupedAnnotations[page] || !state.groupedAnnotations[page][renderType]) {
      return _objectSpread(_objectSpread({}, state), {}, {
        active: {}
      });
    }

    // Find the existing annotation to revert
    const existingAnnotation = state.groupedAnnotations[page][renderType].find(ann => ann.id === id);
    if (!existingAnnotation) {
      return _objectSpread(_objectSpread({}, state), {}, {
        active: {}
      });
    }

    // Transform the reverted annotation to the legacy/editor shape
    const transformed = convertFormAnnotationToLegacyAnnotation(revertedFormState);
    if (!transformed) {
      return _objectSpread(_objectSpread({}, state), {}, {
        active: {}
      });
    }

    // Revert groupedAnnotations
    const mergedAnnotation = _objectSpread(_objectSpread({}, existingAnnotation), transformed);
    const newGroupedAnnotations = updateGroupedAnnotations(state.groupedAnnotations, page, renderType, id, mergedAnnotation);
    return _objectSpread(_objectSpread({}, state), {}, {
      groupedAnnotations: newGroupedAnnotations,
      active: transformed
    });
  }

  // Fallback: just clear the active annotation
  return _objectSpread(_objectSpread({}, state), {}, {
    active: {}
  });
};

/**
 * Remove annotations from groupedAnnotations when their associated form is deleted
 */
const removeAnnotationsOnFormDelete = (state, action) => {
  const {
    ids
  } = action;
  const deletedIds = Array.isArray(ids) ? ids : [ids];

  // If no annotations to remove, return current state
  if (deletedIds.length === 0) {
    return state;
  }

  // Create a new groupedAnnotations object
  const newGroupedAnnotations = {};

  // Iterate through all pages and types
  Object.keys(state.groupedAnnotations).forEach(page => {
    newGroupedAnnotations[page] = {};
    Object.keys(state.groupedAnnotations[page]).forEach(type => {
      // Filter out annotations that match the deleted form IDs
      const filteredAnnotations = state.groupedAnnotations[page][type].filter(annotation => !deletedIds.includes(annotation.id));

      // Only add the type if there are remaining annotations
      if (filteredAnnotations.length > 0) {
        newGroupedAnnotations[page][type] = filteredAnnotations;
      }
    });

    // Only add the page if it has any types with annotations
    if (Object.keys(newGroupedAnnotations[page]).length === 0) {
      delete newGroupedAnnotations[page];
    }
  });

  // Clear active annotation if it was deleted
  const activeWasDeleted = state.active && state.active.id && deletedIds.includes(state.active.id);
  return _objectSpread(_objectSpread({}, state), {}, {
    groupedAnnotations: newGroupedAnnotations,
    active: activeWasDeleted ? {} : state.active
  });
};
const reducers = {
  [constants.SET_EDITOR_STATE]: setEditorState,
  [constants.PERSIST_EDITOR_STATE]: persistEditorState,
  [constants.LOAD_PERSISTED_STATE]: loadPersistedState,
  [constants.CLEAR_EDITOR_STATE]: clearEditorState,
  [constants.LOAD]: loadAnnotations,
  [constants.LOAD_SUCCESS]: loadAnnotationsSuccess,
  [constants.LOAD_FAILURE]: loadAnnotationsFailure,
  [annotationSelectionConstants.SELECT]: select,
  [annotationSelectionConstants.CLEAR_SELECTION]: clearSelection,
  [formConstants.CHANGE_CONTENT]: patchAnnotationsOnFormChange,
  [formConstants.BATCH_CHANGE]: patchAnnotationsOnFormBatchChange,
  [formConstants.UNDO_CHANGES]: revertAnnotationsOnFormUndo,
  [formConstants.UNDO_CHANGES_WITH_STATE]: revertAnnotationsOnFormUndo,
  [formConstants.ANNOTATION_CREATE_SUCCESS]: addNewAnnotation,
  [formConstants.DESTROY_SUCCESS]: removeAnnotationsOnFormDelete
};
export default applyReducer(reducers, initialState);